import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild, effect, inject } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DetailHeaderComponent } from 'src/app/controls/detail-header/detail-header.component';
import { ListHeaderComponent } from 'src/app/controls/list-header/list-header.component';
import { HttpError } from 'src/app/core/http-error';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { IRestApiConfiguration } from 'src/app/models/RestApiCaller/Interfaces/rest-api-configuration.interface';
import { RestApiConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-configuration';
import { Rights } from 'src/app/models/enums/rights';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { RestApiConfigurationsService } from 'src/app/services/restApi/rest-api-configurations.service';
import { ApiConfigurationDetailContext } from './api-configuration-detail-context';
import { ApiConfigurationState } from './api-configuration-wizard/models/api-configuration-state';
import { ApiConfigurationDetailComponent } from './api-configurations-detail/api-configurations-detail.component';
import { ApiConfigurationsListComponent } from './api-configurations-list/api-configurations-list.component';
import { ContextMenuItem } from './common/models/context-menu-item';
import { MultiLevelContextMenuItem } from './common/models/multi-level-context-menu-item';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-apis-configuration',
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ApiConfigurationsListComponent, DetailHeaderComponent, ApiConfigurationDetailComponent, NgxSpinnerModule],
    templateUrl: './api-configurations.component.html',
    styleUrl: './api-configurations.component.scss',
    providers: [ApiConfigurationState],
})
export class ApiConfigurationsComponent implements OnInit {
    private readonly notificationService = inject(NotificationService);
    private readonly restApiConfigurationsService = inject(RestApiConfigurationsService);
    private readonly memoryService = inject(MemoryService);
    private readonly spinner = inject(NgxSpinnerService);
    private readonly attributesService = inject(AttributeDefinitionsService);

    protected readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Reference au composant ApiConfigurationsList.
     */
    @ViewChild(ApiConfigurationsListComponent) apiConfigurationsList: ApiConfigurationsListComponent;

    /**
     * Liste des enums utilisés dans le template
     */
    public FormMode = FormMode;
    public DisplayMode = DisplayMode;

    /**
     * Contexte du formulaire.
     */
    private _context: ApiConfigurationDetailContext = null;

    /**
     * Définit le contexte du formulaire.
     */
    public set context(value: ApiConfigurationDetailContext) {
        this._context = value;

        if (this.context.apiConfigurationContext.restApiConfiguration.contextOfUse === ContextOfUse.WorkflowAction) {
            this.variableContextMenuItem = new MultiLevelContextMenuItem('Root', null, [
                new MultiLevelContextMenuItem('Attributs sytème du dossier', null, this.systemAttributesMenuItems),
                new MultiLevelContextMenuItem('Attributs personnalisés du dossier', null, this.customAttributesMenuItems),
            ]);
        } else {
            this.variableContextMenuItem = new MultiLevelContextMenuItem('Root', null, [
                new MultiLevelContextMenuItem('Attributs sytème du dossier', null, this.systemAttributesMenuItems),
                new MultiLevelContextMenuItem('Attributs personnalisés du dossier', null, this.customAttributesMenuItems),
                new MultiLevelContextMenuItem('Autocomplétion', null, this.autoCompleteMenuItems),
            ]);
        }
    }

    /**
     * Obtient le contexte du formulaire.
     */
    public get context(): ApiConfigurationDetailContext {
        return this._context;
    }

    /**
     * Mode d'affichage de la liste (Liste, Arborescence, formulaire)
     */
    public displayMode = DisplayMode.List;

    /**
     * Indique si le bouton d'ajout/Edition doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Liste des elements du menu contextuel des variables.
     */
    variableContextMenuItems: ContextMenuItem[] = [];
    variableContextMenuItem: MultiLevelContextMenuItem = null;

    systemAttributesMenuItems: MultiLevelContextMenuItem[] = [];
    customAttributesMenuItems: MultiLevelContextMenuItem[] = [];
    autoCompleteMenuItems: MultiLevelContextMenuItem[] = [new MultiLevelContextMenuItem("Texte saisi par l'utilisateur", '{{userInput}}')];

    constructor() {
        effect(
            () => {
                if (this.apiConfigurationState.$apiConfiguration() !== null && this.isApiConfigurationChanged()) {
                    this.onItemUpdated(this.apiConfigurationState.$apiConfiguration());
                }
            },
            { allowSignalWrites: true }
        );
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ViewCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.ViewEdit);

        this.attributesService.List(null, null, null, null).subscribe((x) => {
            x.value
                .sort((x, y) => x.header.localeCompare(y.header))
                .forEach((attribute) => {
                    if (!attribute.custom) {
                        this.systemAttributesMenuItems.push(
                            new MultiLevelContextMenuItem(attribute.header, `{{data.Folder.${attribute.name[0].toUpperCase() + attribute.name.substring(1)}}}`)
                        );
                    } else {
                        this.customAttributesMenuItems.push(new MultiLevelContextMenuItem(attribute.header, `{{data.Folder.CustomAttributes["${attribute.name}"]}}`));
                    }
                });
        });
    }

    /**
     * Appellé lorsque l'utilisateur selectionne une configuration d'API.
     * @param restApiConfiguration Configuration d'API selectionnée.
     */
    public onItemSelected(restApiConfiguration: RestApiConfiguration) {
        this.loadApiConfiguration(restApiConfiguration.id).subscribe((x) => {
            this.apiConfigurationState.$mode.set(FormMode.Reading);
            this.refresh(RestApiConfiguration.createFormCopy(x));
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param restApiConfiguration Configuration d'API sélectionnée.
     */
    public onItemDisplayed(restApiConfiguration: RestApiConfiguration) {
        this.loadApiConfiguration(restApiConfiguration.id).subscribe((x) => {
            this.apiConfigurationState.$mode.set(FormMode.Reading);
            this.refresh(RestApiConfiguration.createFormCopy(x));
        });
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Visualiser' dans le menu contextuel.
     * @param restApiConfiguration Configuration d'API sélectionnée.
     */
    public onItemViewed(restApiConfiguration: RestApiConfiguration) {
        // Cas de la mise à jour.
        this.loadApiConfiguration(restApiConfiguration.id).subscribe((x) => {
            this.apiConfigurationState.$mode.set(FormMode.Reading);
            this.refresh(RestApiConfiguration.createFormCopy(x));
        });
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param restApiConfiguration Configuration d'API sélectionnée.
     */
    public onItemEdited(restApiConfiguration: RestApiConfiguration) {
        // Cas de la mise à jour.
        this.loadApiConfiguration(restApiConfiguration.id).subscribe((x) => {
            this.apiConfigurationState.$mode.set(FormMode.Edition);
            this.refresh(RestApiConfiguration.createFormCopy(x));
        });
    }

    /**
     * Appelé lorsque les données de la configuration d'API ont été mise à jour.
     * Il s'agit des modifications apportées à l'onglet Infos.
     * @param restApiConfiguration : Valeur de la configuration d'API retourné par le formulaire.
     */
    public onItemUpdated(restApiConfiguration: RestApiConfiguration): void {
        this.spinner.show('apiConfiguration-spinner');
        if (this.context.editedRestApiConfigurationId) {
            // Cas de la mise à jour.
            this.restApiConfigurationsService.update(this.context.editedRestApiConfigurationId, restApiConfiguration).subscribe({
                next: (x) => {
                    this.apiConfigurationsList.refresh();

                    this.refreshWithNotification(RestApiConfiguration.createFormCopy(x), "configuration d'API mise à jour");
                    this.spinner.hide('apiConfiguration-spinner');
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour la configuration d'API." + error.message);
                    this.spinner.hide('apiConfiguration-spinner');
                },
            });
        } else {
            // Cas de la création.
            this.restApiConfigurationsService.add(restApiConfiguration).subscribe({
                next: (x) => {
                    this.apiConfigurationsList.refresh();
                    this.refreshWithNotification(RestApiConfiguration.createFormCopy(x), "Configuration d'API créée");
                    this.spinner.hide('apiConfiguration-spinner');
                },
                error: (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer la configuration d'API." + error.message);
                    this.spinner.hide('apiConfiguration-spinner');
                },
            });
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onItemCreated() {
        this.apiConfigurationState.$mode.set(FormMode.Creation);
        this.displayForm(RestApiConfiguration.createFormCopy(new RestApiConfiguration()));
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param restApiConfiguration Configuration d'API sélectionnée.
     */
    public onItemDuplicated(restApiConfiguration: RestApiConfiguration) {
        let duplicatedApiConfiguration = RestApiConfiguration.createFormCopy(restApiConfiguration);
        duplicatedApiConfiguration.id = null;
        this.apiConfigurationState.$mode.set(FormMode.Creation);
        this.displayForm(duplicatedApiConfiguration);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param restApiConfiguration Configuration d'API sélectionnée.
     */
    public onItemDeleted(restApiConfiguration: RestApiConfiguration) {
        this.spinner.show('apiConfiguration-spinner');
        // Suppression effective de la configuration d'API.
        this.restApiConfigurationsService.delete(restApiConfiguration.id).subscribe({
            next: (x) => {
                this.notificationService.displaySuccess(null, "Configuration d'API supprimée");
                this.apiConfigurationsList.refresh();
                this.spinner.hide('apiConfiguration-spinner');
            },
            error: (error: HttpError) => {
                this.notificationService.displayError('Erreur', `Impossible de supprimer la configuration d'API. ${error.message}`);
                this.spinner.hide('apiConfiguration-spinner');
            },
        });
    }

    /**
     * Appellé lors de l'export.
     */
    public onItemsExported() {
        this.apiConfigurationsList.export('ApiConfigurations.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onItemsImported() {
        this.apiConfigurationsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onHelpRequested() {
        this.apiConfigurationsList.displayHelpDialog('apiConfigurations');
    }

    /**
     * Retourne une valeur indiquant si le formulaire est affiché (visible)
     * @returns True si le formulaire est visible.
     */
    public isFormDisplayed(): boolean {
        return DisplayMode.Form === (this.displayMode & DisplayMode.Form);
    }

    /**
     * Cache le formulaire.
     * La liste ou l'arboresence est alors visible.
     */
    public hideForm(): void {
        this.displayMode &= ~DisplayMode.Form;
    }

    /**
     * Définit le mode d'affichage de la page (Form / List).
     * @param mode Mode d'affichage
     */
    public setDisplayMode(mode: DisplayMode) {
        if (mode == DisplayMode.Form) {
            this.displayMode |= mode;
        } else {
            this.displayMode = mode;
        }
    }

    /**
     * Charge les informations d'une configuration d'API.
     * Il s'agit des données affichées dans l'onglet Infos du détail.
     * @param restApiConfigurationId : Id de la configuration d'API à charger.
     * @returns Les informations de la configuration d'API.
     */
    public loadApiConfiguration(restApiConfigurationId: number): Observable<RestApiConfiguration> {
        return this.restApiConfigurationsService.get(restApiConfigurationId);
    }

    /**
     * Affiche Les données d'une configuration d'API dans le formulaire.
     * @param restApiConfiguration : Instance d'une configuration d'API à afficher dans le formulaire.
     */
    displayForm(restApiConfiguration: RestApiConfiguration): void {
        let context = this.fillForm(restApiConfiguration);

        context.apiConfigurationContext.formVisible = true;

        this.context = context;

        /** NEW */
        this.apiConfigurationState.initialApiConfiguration = restApiConfiguration;
        this.apiConfigurationState.$apiConfiguration.set(restApiConfiguration);
        this.apiConfigurationState.variableContextMenuItems = this.variableContextMenuItem;

        /* FIN NEW */

        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param restApiConfiguration : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    public fillForm(restApiConfiguration: IRestApiConfiguration): ApiConfigurationDetailContext {
        let context = this.createContext();
        context.apiConfigurationContext.restApiConfiguration = restApiConfiguration;
        context.editedRestApiConfigurationId = restApiConfiguration.id;
        return context;
    }

    /**
     * Rafraichi la page.
     * @param restApiConfiguration : Valeur du client retourné par le formulaire.
     */
    public refresh(restApiConfiguration: RestApiConfiguration): void {
        this.apiConfigurationsList.refresh();
        this.displayForm(restApiConfiguration);
        this.fillForm(restApiConfiguration);
    }

    /**
     * Rafraichi la page.
     * @param restApiConfiguration : Valeur du client retourné par le formulaire.
     * @param notificationString: Texte de la notification.
     */
    public refreshWithNotification(restApiConfiguration: RestApiConfiguration, notificationString: string): void {
        this.refresh(restApiConfiguration);
        this.notificationService.displaySuccess(null, notificationString);
        this.setDisplayMode(DisplayMode.Form);
    }

    /**
     * Créer un nouveau contexte.
     */
    private createContext(): ApiConfigurationDetailContext {
        let context = new ApiConfigurationDetailContext();
        return context;
    }

    /**
     * Retourneune valeur indiquant si la configuration de l'API a changée
     * @returns true, si la configuration de l'API a changé.
     */
    private isApiConfigurationChanged(): boolean {
        return JSON.stringify(this.apiConfigurationState.$apiConfiguration()) !== JSON.stringify(this.apiConfigurationState.initialApiConfiguration);
    }
}
